import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, TextField, Button, IconButton,
  List, ListItem, ListItemText, ListItemSecondaryAction, ListItemAvatar, Avatar,
  Tabs, Tab, Alert, Snackbar, CircularProgress, Card, CardContent,
  Tooltip, Chip, Stack, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  LinearProgress
} from '@mui/material';
import {
  Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon,
  DragIndicator, ArrowUpward, ArrowDownward, ImageOutlined,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
// Data loading uses Admin API exclusively for reliability

const getApiBaseUrl = () => {
  const environment = process.env.REACT_APP_FIREBASE_ENV || 'test';
  switch (environment) {
    case 'production':
      return 'https://adminapi-a2b5o5ydga-uc.a.run.app';
    case 'test':
      return 'https://adminapi-xjh3gncbvq-uc.a.run.app';
    default:
      return 'https://adminapi-xjh3gncbvq-uc.a.run.app';
  }
};

const getAuthToken = () => localStorage.getItem('admin_token') || '';

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

interface UserInfo {
  id: string;
  displayName: string;
  photoUrl: string;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index}>{value === index && <Box sx={{ p: 3 }}>{children}</Box>}</div>
);

const HomeSettingsPage: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success'
  });

  const [bannerImages, setBannerImages] = useState<Array<{ url: string; linkType: string; linkId: string }>>([]);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [bannerUploadProgress, setBannerUploadProgress] = useState(0);
  const bannerFileInputRef = useRef<HTMLInputElement>(null);
  const [priceImageUploading, setPriceImageUploading] = useState(false);
  const [priceImageUploadProgress, setPriceImageUploadProgress] = useState(0);
  const [popularArtistIds, setPopularArtistIds] = useState<string[]>([]);
  const [newArtistId, setNewArtistId] = useState('');
  const [recommendedUserIds, setRecommendedUserIds] = useState<string[]>([]);
  const [newRecommendedId, setNewRecommendedId] = useState('');
  const [featuredSections, setFeaturedSections] = useState<Array<{ title: string; description: string; productIds: string[] }>>([]);
  const [priceRangeImages, setPriceRangeImages] = useState({ range1_image: '', range2_image: '', range3_image: '' });

  const [userCache, setUserCache] = useState<Record<string, UserInfo>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserInfo[]>([]);
  const [searching, setSearching] = useState(false);
  const fetchedIdsRef = React.useRef<Set<string>>(new Set());

  interface ProductInfo { id: string; title: string; imageUrl: string; price: number; }
  const [productCache, setProductCache] = useState<Record<string, ProductInfo>>({});
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productSearchResults, setProductSearchResults] = useState<ProductInfo[]>([]);
  const [productSearching, setProductSearching] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [activeSectionIdx, setActiveSectionIdx] = useState<number>(-1);
  const productFetchedRef = useRef<Set<string>>(new Set());

  const fetchUsersBatch = useCallback(async (userIds: string[]) => {
    const newIds = userIds.filter(id => !fetchedIdsRef.current.has(id));
    if (newIds.length === 0) return;
    newIds.forEach(id => fetchedIdsRef.current.add(id));
    try {
      const res = await fetch(`${getApiBaseUrl()}/users-brief/batch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userIds: newIds }),
      });
      if (res.ok) {
        const data = await res.json();
        const users: Record<string, UserInfo> = {};
        for (const [id, info] of Object.entries(data.users || {})) {
          const u = info as any;
          users[id] = {
            id,
            displayName: u.displayName || 'ユーザー名なし',
            photoUrl: u.photoUrl || '',
          };
        }
        setUserCache(prev => ({ ...prev, ...users }));
      }
    } catch (e) {
      console.error('Users batch fetch error:', e);
      const fallbacks: Record<string, UserInfo> = {};
      newIds.forEach(id => {
        fallbacks[id] = { id, displayName: '(取得エラー)', photoUrl: '' };
      });
      setUserCache(prev => ({ ...prev, ...fallbacks }));
    }
  }, []);

  const searchUsers = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(`${getApiBaseUrl()}/users/search?q=${encodeURIComponent(searchQuery)}`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
      });
      if (res.ok) {
        const data = await res.json();
        const results: UserInfo[] = (data.users || data || []).map((u: any) => ({
          id: u.id || u.uid,
          displayName: u.displayName || u.name || u.userName || 'ユーザー名なし',
          photoUrl: u.photoUrl || u.avatar || '',
        }));
        setSearchResults(results.slice(0, 10));
      }
    } catch (e) {
      console.error('Search error:', e);
    }
    setSearching(false);
  };

  const fetchProductsBatch = useCallback(async (productIds: string[]) => {
    const newIds = productIds.filter(id => !productFetchedRef.current.has(id));
    if (newIds.length === 0) return;
    newIds.forEach(id => productFetchedRef.current.add(id));
    try {
      const res = await fetch(`${getApiBaseUrl()}/products/batch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ productIds: newIds }),
      });
      if (res.ok) {
        const data = await res.json();
        const products: Record<string, ProductInfo> = {};
        for (const [id, info] of Object.entries(data.products || {})) {
          const p = info as any;
          products[id] = {
            id,
            title: p.title || p.productName || '無題',
            imageUrl: p.imageUrl || (p.images && p.images[0]) || '',
            price: p.price || 0,
          };
        }
        setProductCache(prev => ({ ...prev, ...products }));
      }
    } catch (e) {
      console.error('Products batch fetch error:', e);
      const fallbacks: Record<string, ProductInfo> = {};
      newIds.forEach(id => {
        fallbacks[id] = { id, title: '(取得エラー)', imageUrl: '', price: 0 };
      });
      setProductCache(prev => ({ ...prev, ...fallbacks }));
    }
  }, []);

  const searchProducts = async () => {
    if (!productSearchQuery.trim()) return;
    setProductSearching(true);
    try {
      const res = await fetch(`${getApiBaseUrl()}/products?search=${encodeURIComponent(productSearchQuery)}&limit=20`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
      });
      if (res.ok) {
        const data = await res.json();
        const results: ProductInfo[] = (data.products || []).map((p: any) => ({
          id: p.id,
          title: p.title || p.productName || '無題',
          imageUrl: p.images?.[0] || p.image || '',
          price: p.price || 0,
        }));
        setProductSearchResults(results);
      }
    } catch (e) {
      console.error('Product search error:', e);
    }
    setProductSearching(false);
  };

  useEffect(() => {
    const allProductIds = featuredSections.flatMap(s => s.productIds);
    const newProductIds = allProductIds.filter(id => !productFetchedRef.current.has(id));
    if (newProductIds.length > 0) fetchProductsBatch(newProductIds);
  }, [featuredSections, fetchProductsBatch]);

  useEffect(() => { loadSettings(); }, []);

  useEffect(() => {
    const allIds = [...popularArtistIds, ...recommendedUserIds];
    const newIds = allIds.filter(id => !fetchedIdsRef.current.has(id));
    if (newIds.length > 0) fetchUsersBatch(newIds);
  }, [popularArtistIds, recommendedUserIds, fetchUsersBatch]);

  const fetchSettingsDoc = async (docId: string) => {
    const res = await fetch(`${getApiBaseUrl()}/home-settings/${docId}`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    if (!res.ok) return null;
    return res.json();
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const [bannerData, artistsData, recommendedData, sectionsData, priceData] = await Promise.all([
        fetchSettingsDoc('banner'),
        fetchSettingsDoc('popular_artists'),
        fetchSettingsDoc('recommended_users'),
        fetchSettingsDoc('featured_sections'),
        fetchSettingsDoc('price_range_images'),
      ]);

      if (bannerData?.images) setBannerImages(bannerData.images);
      if (artistsData?.userIds) setPopularArtistIds(artistsData.userIds);
      if (recommendedData?.userIds) setRecommendedUserIds(recommendedData.userIds);
      if (sectionsData?.sections) setFeaturedSections(sectionsData.sections);
      if (priceData) {
        setPriceRangeImages({
          range1_image: priceData.range1_image || '',
          range2_image: priceData.range2_image || '',
          range3_image: priceData.range3_image || '',
        });
      }
    } catch (e) {
      console.error('Error loading settings:', e);
    }
    setLoading(false);
  };

  const saveSection = async (docId: string, data: any, label: string) => {
    try {
      const res = await fetch(`${getApiBaseUrl()}/home-settings/${docId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        setSnackbar({ open: true, message: `${label}を保存しました`, severity: 'success' });
      } else {
        throw new Error('Save failed');
      }
    } catch (e) {
      setSnackbar({ open: true, message: '保存に失敗しました', severity: 'error' });
    }
  };

  const uploadImageViaFunction = async (file: File, folder: string): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
    );

    const timestamp = Date.now();
    const ext = file.name.split('.').pop() || 'jpg';
    const fileName = `${timestamp}_${Math.random().toString(36).slice(2)}.${ext}`;

    const res = await fetch(`${getApiBaseUrl()}/upload-image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fileData: base64,
        fileName,
        contentType: file.type,
        folder,
      }),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || 'Upload failed');
    }

    const data = await res.json();
    return data.url;
  };

  const handleBannerFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setSnackbar({ open: true, message: 'JPEG, PNG, WebP, GIF画像のみアップロード可能です', severity: 'error' });
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setSnackbar({ open: true, message: '画像サイズは5MB以下にしてください', severity: 'error' });
      return;
    }

    setBannerUploading(true);
    setBannerUploadProgress(0);

    try {
      setBannerUploadProgress(30);
      const downloadUrl = await uploadImageViaFunction(file, 'home_banners');
      setBannerImages(prev => [...prev, { url: downloadUrl, linkType: '', linkId: '' }]);
      setBannerUploadProgress(100);
      setSnackbar({ open: true, message: 'バナー画像をアップロードしました', severity: 'success' });
    } catch (error) {
      console.error('Upload error:', error);
      setSnackbar({ open: true, message: '画像のアップロードに失敗しました', severity: 'error' });
    } finally {
      setBannerUploading(false);
      setBannerUploadProgress(0);
    }

    if (bannerFileInputRef.current) {
      bannerFileInputRef.current.value = '';
    }
  };

  const handlePriceRangeFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    rangeKey: 'range1_image' | 'range2_image' | 'range3_image'
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setSnackbar({ open: true, message: 'JPEG, PNG, WebP, GIF画像のみアップロード可能です', severity: 'error' });
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setSnackbar({ open: true, message: '画像サイズは5MB以下にしてください', severity: 'error' });
      return;
    }

    setPriceImageUploading(true);
    setPriceImageUploadProgress(0);

    try {
      setPriceImageUploadProgress(30);
      const downloadUrl = await uploadImageViaFunction(file, 'price_range_images');
      setPriceRangeImages(prev => ({ ...prev, [rangeKey]: downloadUrl }));
      setPriceImageUploadProgress(100);
      setSnackbar({ open: true, message: '画像をアップロードしました', severity: 'success' });
    } catch (error) {
      console.error('Upload error:', error);
      setSnackbar({ open: true, message: '画像のアップロードに失敗しました', severity: 'error' });
    } finally {
      setPriceImageUploading(false);
      setPriceImageUploadProgress(0);
    }
  };

  const UserListItem: React.FC<{ userId: string; onRemove: () => void }> = ({ userId, onRemove }) => {
    const info = userCache[userId];
    return (
      <ListItem sx={{ border: '1px solid #eee', borderRadius: 1, mb: 1 }}>
        <ListItemAvatar>
          <Avatar src={info?.photoUrl} sx={{ width: 40, height: 40 }}>
            {info?.displayName?.[0] || '?'}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={<Typography fontWeight="bold">{info?.displayName || '読み込み中...'}</Typography>}
          secondary={<Typography variant="caption" color="text.secondary">ID: {userId}</Typography>}
        />
        <ListItemSecondaryAction>
          <Tooltip title="削除">
            <IconButton edge="end" onClick={onRemove} color="error">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </ListItemSecondaryAction>
      </ListItem>
    );
  };

  const UserSearchBox: React.FC<{ onAdd: (id: string) => void; placeholder: string }> = ({ onAdd, placeholder }) => (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>ユーザーを追加</Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <TextField
          fullWidth size="small" placeholder="ユーザー名またはIDで検索"
          value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
        />
        <Button variant="outlined" startIcon={<SearchIcon />} onClick={searchUsers} disabled={searching}>
          検索
        </Button>
      </Stack>
      {searchResults.length > 0 && (
        <List dense sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
          {searchResults.map(user => (
            <ListItem key={user.id} component="div"
              sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' } }}
              onClick={() => { onAdd(user.id); setSearchResults([]); setSearchQuery(''); }}>
              <ListItemAvatar><Avatar src={user.photoUrl} sx={{ width: 32, height: 32 }}>{user.displayName[0]}</Avatar></ListItemAvatar>
              <ListItemText primary={user.displayName} secondary={user.id} />
              <Chip label="追加" size="small" color="primary" />
            </ListItem>
          ))}
        </List>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        または直接IDを入力:
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
        <TextField fullWidth size="small" placeholder={placeholder} value={newArtistId}
          onChange={(e) => setNewArtistId(e.target.value)} />
        <Button variant="contained" size="small" startIcon={<AddIcon />}
          onClick={() => { if (newArtistId) { onAdd(newArtistId); setNewArtistId(''); } }}>
          追加
        </Button>
      </Stack>
    </Paper>
  );

  const ImagePreview: React.FC<{ url: string }> = ({ url }) => {
    if (!url) return null;
    return (
      <Box sx={{ mt: 1, mb: 1 }}>
        <img src={url} alt="プレビュー"
          style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 8, objectFit: 'cover', border: '1px solid #ddd' }}
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      </Box>
    );
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>ホーム画面設定</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        各タブで設定を変更後、「保存」ボタンを押してください。保存しないと変更は反映されません。
      </Alert>

      <Paper sx={{ mt: 2 }}>
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="バナー画像" />
          <Tab label="人気の作家" />
          <Tab label="おすすめ" />
          <Tab label="特集" />
          <Tab label="価格帯画像" />
        </Tabs>

        <TabPanel value={tabIndex} index={0}>
          <Typography variant="h6" gutterBottom>トップバナー画像</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ホーム画面上部に表示されるスライドバナー画像を管理します。上から順に表示されます。
          </Typography>
          <List>
            {bannerImages.map((img, idx) => (
              <ListItem key={idx} sx={{ border: '1px solid #eee', borderRadius: 1, mb: 1, flexDirection: 'column', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1 }}>
                    <IconButton
                      size="small"
                      disabled={idx === 0}
                      onClick={() => {
                        const newList = [...bannerImages];
                        [newList[idx - 1], newList[idx]] = [newList[idx], newList[idx - 1]];
                        setBannerImages(newList);
                      }}
                    >
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={idx === bannerImages.length - 1}
                      onClick={() => {
                        const newList = [...bannerImages];
                        [newList[idx], newList[idx + 1]] = [newList[idx + 1], newList[idx]];
                        setBannerImages(newList);
                      }}
                    >
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                  </Box>
                  <ListItemText
                    primary={<Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{img.url}</Typography>}
                    secondary={`リンク先: ${img.linkType || '設定なし'}　（${idx + 1}/${bannerImages.length}）`}
                  />
                  <IconButton onClick={() => setBannerImages(bannerImages.filter((_, i) => i !== idx))} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <ImagePreview url={img.url} />
              </ListItem>
            ))}
          </List>
          <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>バナー画像を追加</Typography>
            <input
              ref={bannerFileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              style={{ display: 'none' }}
              onChange={handleBannerFileUpload}
            />
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={() => bannerFileInputRef.current?.click()}
              disabled={bannerUploading}
              sx={{ width: '100%', height: 80, border: '2px dashed #ccc', '&:hover': { border: '2px dashed #1976d2' } }}
            >
              {bannerUploading ? 'アップロード中...' : '画像を選択してアップロード'}
            </Button>
            {bannerUploading && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress variant="determinate" value={bannerUploadProgress} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {Math.round(bannerUploadProgress)}%
                </Typography>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              対応形式: JPEG, PNG, WebP, GIF（最大5MB）
            </Typography>
          </Paper>
          <Button variant="contained" color="primary" size="large" sx={{ mt: 3 }}
            onClick={() => saveSection('banner', { images: bannerImages }, 'バナー設定')}>
            保存
          </Button>
        </TabPanel>

        <TabPanel value={tabIndex} index={1}>
          <Typography variant="h6" gutterBottom>人気の作家</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ホーム画面の「人気の作家」セクションに表示するユーザーを選択します。作品と共に表示されます。
          </Typography>
          <List>
            {popularArtistIds.map((id, idx) => (
              <UserListItem key={idx} userId={id} onRemove={() => setPopularArtistIds(popularArtistIds.filter((_, i) => i !== idx))} />
            ))}
            {popularArtistIds.length === 0 && (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>まだ作家が登録されていません</Typography>
            )}
          </List>
          <UserSearchBox
            placeholder="ユーザーIDを入力"
            onAdd={(id) => { if (!popularArtistIds.includes(id)) setPopularArtistIds([...popularArtistIds, id]); }}
          />
          <Button variant="contained" color="primary" size="large" sx={{ mt: 3 }}
            onClick={() => saveSection('popular_artists', { userIds: popularArtistIds }, '人気の作家')}>
            保存
          </Button>
        </TabPanel>

        <TabPanel value={tabIndex} index={2}>
          <Typography variant="h6" gutterBottom>おすすめユーザー</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            「おすすめ」タブに表示するユーザーを選択します。
          </Typography>
          <List>
            {recommendedUserIds.map((id, idx) => (
              <UserListItem key={idx} userId={id} onRemove={() => setRecommendedUserIds(recommendedUserIds.filter((_, i) => i !== idx))} />
            ))}
            {recommendedUserIds.length === 0 && (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>まだユーザーが登録されていません</Typography>
            )}
          </List>
          <UserSearchBox
            placeholder="ユーザーIDを入力"
            onAdd={(id) => { if (!recommendedUserIds.includes(id)) setRecommendedUserIds([...recommendedUserIds, id]); }}
          />
          <Button variant="contained" color="primary" size="large" sx={{ mt: 3 }}
            onClick={() => saveSection('recommended_users', { userIds: recommendedUserIds }, 'おすすめユーザー')}>
            保存
          </Button>
        </TabPanel>

        <TabPanel value={tabIndex} index={3}>
          <Typography variant="h6" gutterBottom>特集セクション</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ホーム画面に表示する特集を管理します。「アリクイピックアップ」「はじめてのアートにおすすめ」に加えて、独自の特集を追加できます。
          </Typography>

          {featuredSections.length === 0 && (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', mb: 2 }}>
              <Typography color="text.secondary">まだ特集が追加されていません</Typography>
              <Typography variant="caption" color="text.secondary">下の「特集を追加」ボタンから新しい特集を作成できます</Typography>
            </Paper>
          )}

          {featuredSections.map((section, idx) => (
            <Card key={idx} sx={{ mb: 3, border: '1px solid #e0e0e0', position: 'relative' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    特集 #{idx + 1} {section.title && `- ${section.title}`}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="上に移動">
                      <span>
                        <IconButton size="small" disabled={idx === 0}
                          onClick={() => {
                            const updated = [...featuredSections];
                            [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
                            setFeaturedSections(updated);
                          }}>
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="下に移動">
                      <span>
                        <IconButton size="small" disabled={idx === featuredSections.length - 1}
                          onClick={() => {
                            const updated = [...featuredSections];
                            [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
                            setFeaturedSections(updated);
                          }}>
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="この特集を削除">
                      <IconButton size="small" color="error"
                        onClick={() => setFeaturedSections(featuredSections.filter((_, i) => i !== idx))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>

                <Stack spacing={2}>
                  <TextField fullWidth size="small" label="特集タイトル" placeholder="例: 夏の涼しげアート"
                    value={section.title}
                    onChange={(e) => {
                      const updated = [...featuredSections];
                      updated[idx] = { ...updated[idx], title: e.target.value };
                      setFeaturedSections(updated);
                    }} />
                  <TextField fullWidth size="small" label="説明文（任意）" placeholder="例: 暑い夏にぴったりの作品を集めました"
                    value={section.description}
                    onChange={(e) => {
                      const updated = [...featuredSections];
                      updated[idx] = { ...updated[idx], description: e.target.value };
                      setFeaturedSections(updated);
                    }} />

                  <Divider />
                  <Typography variant="subtitle2">
                    商品一覧（{section.productIds.length}件）
                  </Typography>

                  {section.productIds.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      商品が追加されていません。下の「商品を検索して追加」ボタンから追加してください。
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {section.productIds.map((pid, pidIdx) => {
                        const pInfo = productCache[pid];
                        return (
                          <Paper key={pidIdx} variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', sm: 'calc(50% - 4px)', md: 'calc(33.33% - 6px)' } }}>
                            {pInfo?.imageUrl ? (
                              <Avatar variant="rounded" src={pInfo.imageUrl} sx={{ width: 48, height: 48 }} />
                            ) : (
                              <Avatar variant="rounded" sx={{ width: 48, height: 48, bgcolor: '#f0f0f0' }}>
                                <ImageOutlined color="disabled" />
                              </Avatar>
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" noWrap fontWeight="bold">
                                {pInfo?.title || '読み込み中...'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {pInfo?.price ? `¥${pInfo.price.toLocaleString()}` : ''}
                              </Typography>
                            </Box>
                            <Tooltip title="削除">
                              <IconButton size="small" color="error"
                                onClick={() => {
                                  const updated = [...featuredSections];
                                  updated[idx] = { ...updated[idx], productIds: updated[idx].productIds.filter((_, i) => i !== pidIdx) };
                                  setFeaturedSections(updated);
                                }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Paper>
                        );
                      })}
                    </Box>
                  )}

                  <Button variant="outlined" size="small" startIcon={<SearchIcon />}
                    onClick={() => { setActiveSectionIdx(idx); setProductDialogOpen(true); setProductSearchResults([]); setProductSearchQuery(''); }}>
                    商品を検索して追加
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ))}

          <Button variant="outlined" startIcon={<AddIcon />} sx={{ mb: 2 }} fullWidth
            onClick={() => setFeaturedSections([...featuredSections, { title: '', description: '', productIds: [] }])}>
            新しい特集を追加
          </Button>
          <Box>
            <Button variant="contained" color="primary" size="large"
              onClick={() => saveSection('featured_sections', { sections: featuredSections }, '特集設定')}>
              保存
            </Button>
          </Box>

          <Dialog open={productDialogOpen} onClose={() => setProductDialogOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>商品を検索して追加</DialogTitle>
            <DialogContent>
              <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 2 }}>
                <TextField fullWidth size="small" placeholder="商品名またはIDで検索"
                  value={productSearchQuery}
                  onChange={(e) => setProductSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchProducts()} />
                <Button variant="contained" startIcon={<SearchIcon />} onClick={searchProducts} disabled={productSearching}>
                  検索
                </Button>
              </Stack>
              {productSearching && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>}
              {productSearchResults.length > 0 && (
                <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {productSearchResults.map(product => {
                    const alreadyAdded = activeSectionIdx >= 0 && featuredSections[activeSectionIdx]?.productIds.includes(product.id);
                    return (
                      <ListItem key={product.id} sx={{ border: '1px solid #eee', borderRadius: 1, mb: 1, opacity: alreadyAdded ? 0.5 : 1 }}>
                        <ListItemAvatar>
                          {product.imageUrl ? (
                            <Avatar variant="rounded" src={product.imageUrl} sx={{ width: 56, height: 56 }} />
                          ) : (
                            <Avatar variant="rounded" sx={{ width: 56, height: 56, bgcolor: '#f0f0f0' }}>
                              <ImageOutlined />
                            </Avatar>
                          )}
                        </ListItemAvatar>
                        <ListItemText
                          primary={<Typography fontWeight="bold">{product.title}</Typography>}
                          secondary={`¥${product.price.toLocaleString()} | ID: ${product.id}`}
                          sx={{ ml: 1 }}
                        />
                        <Button variant="contained" size="small" disabled={alreadyAdded}
                          onClick={() => {
                            if (activeSectionIdx >= 0 && !alreadyAdded) {
                              const updated = [...featuredSections];
                              updated[activeSectionIdx] = {
                                ...updated[activeSectionIdx],
                                productIds: [...updated[activeSectionIdx].productIds, product.id]
                              };
                              setFeaturedSections(updated);
                              setProductCache(prev => ({ ...prev, [product.id]: product }));
                            }
                          }}>
                          {alreadyAdded ? '追加済み' : '追加'}
                        </Button>
                      </ListItem>
                    );
                  })}
                </List>
              )}
              {!productSearching && productSearchResults.length === 0 && productSearchQuery && (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  検索結果がありません。キーワードを変えてお試しください。
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setProductDialogOpen(false)}>閉じる</Button>
            </DialogActions>
          </Dialog>
        </TabPanel>

        <TabPanel value={tabIndex} index={4}>
          <Typography variant="h6" gutterBottom>価格帯背景画像</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            「値段から探す」セクションの背景画像を設定します。
          </Typography>
          <Stack spacing={3}>
            {([
              { key: 'range1_image' as const, label: '〜¥10,000 の背景画像' },
              { key: 'range2_image' as const, label: '¥10,000〜¥50,000 の背景画像' },
              { key: 'range3_image' as const, label: '¥50,000〜 の背景画像' },
            ]).map(({ key, label }) => (
              <Paper key={key} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>{label}</Typography>
                <ImagePreview url={priceRangeImages[key]} />
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  style={{ display: 'none' }}
                  id={`price-range-upload-${key}`}
                  onChange={(e) => handlePriceRangeFileUpload(e, key)}
                />
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => document.getElementById(`price-range-upload-${key}`)?.click()}
                    disabled={priceImageUploading}
                  >
                    画像を変更
                  </Button>
                  {priceRangeImages[key] && (
                    <Button
                      variant="text"
                      size="small"
                      color="error"
                      onClick={() => setPriceRangeImages(prev => ({ ...prev, [key]: '' }))}
                    >
                      削除
                    </Button>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
          <Button variant="contained" color="primary" size="large" sx={{ mt: 3 }}
            onClick={() => saveSection('price_range_images', priceRangeImages, '価格帯画像')}>
            保存
          </Button>
        </TabPanel>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default HomeSettingsPage;
